// The org-wide expert pool (SPEC-v2 X1-X4; API.md 7.1, endpoints 44-48),
// and the v2 changes to the contract's expert list ("engagements", API.md 6.2).
import { z } from "zod";
import { countryCode } from "../countries";
import { EXPERT_CATEGORIES, type ContractStatus, type ExpertCategory, type TorStatus } from "../enums";
import { LIMITS } from "../limits";
import { cents, email, halfStep, httpUrlOrEmpty, isoDate, optionalText, queryBoolean, requiredText, searchQuery, uuid } from "./common";

// ---------- Requests ----------

const poolExpertFields = {
  name: requiredText(LIMITS.expertName, "Name"),
  email: email.nullable(),
  phone: optionalText(LIMITS.phone, "Phone"),
  country: countryCode.nullable(),
  expertise: optionalText(LIMITS.expertise, "Expertise"),
  languages: optionalText(LIMITS.languages, "Languages"),
  availabilityNote: optionalText(LIMITS.availabilityNote, "Availability note"),
  cvUrl: httpUrlOrEmpty(LIMITS.url),
};

/** 45. POST /api/expert-pool */
export const createPoolExpertBody = z
  .object({
    name: poolExpertFields.name,
    email: poolExpertFields.email.default(null),
    phone: poolExpertFields.phone.default(""),
    country: poolExpertFields.country.default(null),
    expertise: poolExpertFields.expertise.default(""),
    languages: poolExpertFields.languages.default(""),
    availabilityNote: poolExpertFields.availabilityNote.default(""),
    cvUrl: poolExpertFields.cvUrl.default(""),
  })
  .strict();
export type CreatePoolExpertBody = z.input<typeof createPoolExpertBody>;
export type CreatePoolExpertInput = z.output<typeof createPoolExpertBody>;

/** 47. PATCH /api/expert-pool/:id (any subset; `{}` answers EMPTY_PATCH) */
export const updatePoolExpertBody = z
  .object({
    name: poolExpertFields.name.optional(),
    email: poolExpertFields.email.optional(),
    phone: poolExpertFields.phone.optional(),
    country: poolExpertFields.country.optional(),
    expertise: poolExpertFields.expertise.optional(),
    languages: poolExpertFields.languages.optional(),
    availabilityNote: poolExpertFields.availabilityNote.optional(),
    cvUrl: poolExpertFields.cvUrl.optional(),
  })
  .strict();
export type UpdatePoolExpertBody = z.infer<typeof updatePoolExpertBody>;

/** 44. GET /api/expert-pool?q&freeFrom&freeTo&onlyFree */
export const poolListQuery = z
  .object({
    q: searchQuery.optional(),
    freeFrom: isoDate.optional(),
    freeTo: isoDate.optional(),
    onlyFree: queryBoolean.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if ((v.freeFrom === undefined) !== (v.freeTo === undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [v.freeFrom === undefined ? "freeFrom" : "freeTo"],
        message: "Give both dates of the range, or neither.",
      });
    }
    if (v.freeFrom && v.freeTo && v.freeTo < v.freeFrom) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["freeTo"], message: "The end date must be on or after the start date." });
    }
  });
export type PoolListQuery = z.infer<typeof poolListQuery>;

// ----- engagements: v2 shapes of v1 endpoints 29 and 30 (API.md 6.2) -----

const engagementFields = {
  category: z.enum(EXPERT_CATEGORIES),
  positionTitle: optionalText(LIMITS.positionTitle, "Position"),
  dayBudget: halfStep(LIMITS.dayBudgetMax).nullable(),
  feeRateCents: cents(LIMITS.feeRateCentsMax).nullable(),
};

/**
 * 29. POST /api/contracts/:id/experts, v2: EITHER `poolExpertId` (pick from
 * the pool) OR `name` (+ optional `email`) to create a pool expert in the same
 * transaction. Replaces v1's `createExpertBody` once the API builder switches
 * the route.
 */
export const createEngagementBody = z
  .object({
    poolExpertId: uuid.optional(),
    name: requiredText(LIMITS.expertName, "Name").optional(),
    email: email.nullable().optional(),
    category: engagementFields.category,
    positionTitle: engagementFields.positionTitle.default(""),
    dayBudget: engagementFields.dayBudget.default(null),
    feeRateCents: engagementFields.feeRateCents.default(null),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.poolExpertId !== undefined && (v.name !== undefined || v.email !== undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["poolExpertId"],
        message: "Pick an expert from the pool, or type a new person, not both.",
      });
    }
    if (v.poolExpertId === undefined && v.name === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["name"], message: "Name is required." });
    }
  });
export type CreateEngagementBody = z.input<typeof createEngagementBody>;
export type CreateEngagementInput = z.output<typeof createEngagementBody>;

/**
 * 30. PATCH /api/experts/:id, v2: `name` and `email` are no longer accepted
 * (they are edited on the pool profile); `poolExpertId` re-links the
 * engagement. Replaces v1's `updateExpertBody` once the route is switched.
 */
export const updateEngagementBody = z
  .object({
    poolExpertId: uuid.optional(),
    category: engagementFields.category.optional(),
    positionTitle: engagementFields.positionTitle.optional(),
    dayBudget: engagementFields.dayBudget.optional(),
    feeRateCents: engagementFields.feeRateCents.optional(),
  })
  .strict();
export type UpdateEngagementBody = z.infer<typeof updateEngagementBody>;

// ---------- Responses ----------

export type PoolExpertRef = { id: string; name: string };

/** One contract_expert row seen from the pool. */
export type Engagement = {
  contractExpertId: string;
  contract: { id: string; name: string; reference: string; status: ContractStatus };
  category: ExpertCategory;
  positionTitle: string;
  dayBudget: number | null;
  feeRateCents: number | null;
  /** as Expert.plannedDays */
  plannedDays: number;
  /** ARCHITECTURE 18.1, all dates */
  loggedDays: number;
};

export type BusyItem = {
  torId: string;
  title: string;
  activityCode: string;
  contractReference: string;
  startDate: string | null;
  endDate: string | null;
  status: TorStatus;
};

export type PoolExpertSummary = {
  id: string;
  name: string;
  email: string | null;
  phone: string;
  country: string | null;
  expertise: string;
  languages: string;
  availabilityNote: string;
  cvUrl: string;
  /** by reference */
  contracts: { id: string; reference: string }[];
  /** Σ over engagements */
  plannedDays: number;
  /** Σ over engagements */
  loggedDays: number;
  /** null when no range was asked */
  availability: { free: boolean; busy: BusyItem[] } | null;
  createdAt: string;
  updatedAt: string;
};

export type PoolExpertDetail = Omit<PoolExpertSummary, "availability"> & {
  /** by contract reference */
  engagements: Engagement[];
  /** every non-withdrawn ToR with an expert-days line for them, newest start first */
  plannedOn: BusyItem[];
  /** delete = editor and no engagements */
  can: { edit: boolean; delete: boolean };
};
